import { Suspense, lazy, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import AOS from "aos";
import { ScrollToTop } from "./components/ScrollToTop";
import { ErrorBoundary } from "./components/ErrorBoundary";
import LandingPage from "./pages/LandingPage";
// Not lazy: this is the fallback when a lazy chunk fails to load.
import NotFoundPage from "./pages/NotFoundPage";
import { getAppUrl } from "./env";
import { DEFAULT_LOCALE, LOCALE_META, LOCALES } from "@thyra/landing-i18n";

// Secondary pages are code-split so the home bundle doesn't ship them.
// Prerendering still renders them fully: entry-server uses the async
// prerender API, which waits for lazy chunks.
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const CookiesPage = lazy(() => import("./pages/CookiesPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const BusinessPage = lazy(() => import("./pages/BusinessPage"));
const BlogIndexPage = lazy(() => import("./pages/BlogIndexPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const BlogTagPage = lazy(() => import("./pages/BlogTagPage"));

function AppRedirect() {
  useEffect(() => {
    window.location.replace(getAppUrl());
  }, []);
  return null;
}

function AOSInit() {
  const location = useLocation();
  useEffect(() => {
    AOS.init({ duration: 600, once: true, offset: 48 });
  }, []);
  useEffect(() => {
    AOS.refresh();
  }, [location.pathname]);
  return null;
}

const SECTION_REDIRECTS = [
  { from: "/home", hash: "home" },
  { from: "/product", hash: "product" },
  { from: "/features", hash: "product" },
  { from: "/pricing", hash: "pricing" },
  { from: "/faq", hash: "faq" },
] as const;

/** Mounted once at the root (English) and once under each locale prefix. */
function localizedRoutes() {
  return (
    <>
      <Route index element={<LandingPage />} />
      <Route path="about" element={<AboutPage />} />
      <Route path="business" element={<BusinessPage />} />
      <Route path="team" element={<TeamPage />} />
      <Route path="contact" element={<ContactPage />} />
      {/* The blog exists in every locale (see LOCALIZED_SECTIONS). Posts are
          prerendered under their own language's URL; a post published after
          the last build, or an English post read under another locale, is
          served the blog shell (server.mjs) and rendered client-side. */}
      <Route path="blog" element={<BlogIndexPage />} />
      <Route path="blog/tag/:tag" element={<BlogTagPage />} />
      <Route path="blog/:slug" element={<BlogPostPage />} />
    </>
  );
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <AOSInit />
      <ErrorBoundary>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/">{localizedRoutes()}</Route>
            {LOCALES.filter((locale) => locale !== DEFAULT_LOCALE).map(
              (locale) => (
                <Route key={locale} path={`/${LOCALE_META[locale].slug}`}>
                  {localizedRoutes()}
                </Route>
              ),
            )}
            {/* Legal pages are English-only: the English text is the binding
                version, so they have no locale-prefixed URLs. */}
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/early-access" element={<AppRedirect />} />
            <Route path="/onboarding" element={<AppRedirect />} />
            <Route path="/cookies" element={<CookiesPage />} />
            {SECTION_REDIRECTS.map(({ from, hash }) => (
              <Route
                key={from}
                path={from}
                element={<Navigate to={`/#${hash}`} replace />}
              />
            ))}
            {/* Prerendered to dist/404.html so `serve` returns a real 404
                status for unknown paths (see scripts/prerender.mjs). */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </>
  );
}
